import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import TextInput from '../../../../elements/TextInput';
import { Attachment, Check, Circle, Close } from '@mui/icons-material';
import { Container, IconButton, Typography } from '@mui/material';
import { getDisplayUserName } from '../../../../../lib/userUtils';
import CustomIconButton from '../../../../elements/CustomIconButton';
import Modal from '../../../../elements/Modal';
import AttachmentTable from '../../../../elements/common/attachments/AttachmentTable';
import { attachmentTableHeaders } from '../../../test-case/testCaseTableHead';
import { renderLinks } from '../../../../../lib/utils';

const TestStepItem = ({
  script,
  index,
  statuses,
  isEditing,
  defaultStatus,
  handleUpdateStep,
  loading,
  showUri,
  isSelected,
  onSelectRow,
  isRowDisabled,
}) => {
  const [editingField, setEditingField] = useState(null);
  const [tempActualResult, setTempActualResult] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openAttachments, setOpenAttachments] = useState(false);
  const currentStatus = script?.status_details || defaultStatus;
  const [order, setOrder] = useState('desc');

  const componentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        handleCancelClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onDropdownChange = async (statusId) => {
    await handleUpdateStep({ status: statusId }, script.id);
    setDropdownOpen(false);
    const selectedStatus = statuses.find((s) => s.id === statusId);

    if (selectedStatus?.name?.toLowerCase() === "fail") {
      if (typeof script?.onStepFailed === "function") {
        script.onStepFailed();
      }
    }
  };

  const onActualResultChange = (id, value) => {
    setTempActualResult(value);
  };

  const handleActualResultClick = () => {
    setEditingField(script.id);
    setTempActualResult(script?.actualResult || '');
  };

  const handleSaveClick = async () => {
    await handleUpdateStep({ actualResult: tempActualResult }, script.id);
    setEditingField(null);
  };

  const handleCancelClick = () => {
    setEditingField(null);
    setTempActualResult('');
  };

  const renderCell = (content, isLinkable = false) => (
    <td className="wrap-cell">
      {isLinkable ? renderLinks(content || '') : (content || 'None')}
    </td>
  );

  const attachments = script?.teststeps?.attachments || [];
  const isActualResultEditable = isEditing;

  return (
    <>
      <tr ref={componentRef}>
        <td style={{ width: '40px', textAlign: 'center' }}>
         <input
         type="checkbox"
         checked={isSelected}
         disabled={isRowDisabled?.(script)} 
         onChange={() => onSelectRow(script.id)}
        />
        </td>
        {renderCell(script.teststeps?.s_no)}
        {renderCell(script.teststeps?.step, true)}
        {renderCell(script.teststeps?.test_data, true)}
        {renderCell(script.teststeps?.expected_result, true)}
        {showUri && (
          <td className="wrap-cell">
            {script.teststeps?.uri ? (
              <a
                href={script.teststeps.uri}
                target="_blank"
                rel="noopener noreferrer"
                className="table-link"
                title="Open URI in new tab"
              >
                {script.teststeps.uri}
              </a>
            ) : (
              'None'
            )}
          </td>
        )}
        <td className="wrap-cell">
          {isActualResultEditable && editingField === script.id ? (
            <div>
              <TextInput
                id="actualResult"
                value={tempActualResult}
                onChange={onActualResultChange}
                maxLength={500}
                placeholder="Enter actual result"
                type="textarea"
                autoFocus
                rows={3}
              />
              <div className="d-flex justify-content-center mt-1">
                <IconButton title="Save" onClick={handleSaveClick}>
                  <Check sx={{ color: 'green' }} />
                </IconButton>
                <IconButton title="Cancel" onClick={handleCancelClick}>
                  <Close sx={{ color: 'grey' }} />
                </IconButton>
              </div>
            </div>
          ) : (
          <div
            className={`actual-result-readonly ${
              isActualResultEditable ? 'clickable' : ''
            }`}
            onClick={isActualResultEditable ? handleActualResultClick : undefined} 
            title={isActualResultEditable ? 'Click to update actual result' : ''}>
            {script?.actual_result ? (
              renderLinks(script?.actual_result)
            ) : (
              isActualResultEditable ? (
                <span style={{ color: 'grey', fontSize: '0.7em' }}>Click to add actual result</span>
              ) : (
                <span style={{ color: 'grey' }}>None</span>
              )
            )}
          </div>
          )}
        </td>
        <td className="wrap-cell" style={{ whiteSpace: 'nowrap' }}>
          {isEditing ? (
            <Dropdown
              autoClose="outside"
              show={dropdownOpen}
              onToggle={(isOpen) => setDropdownOpen(isOpen)}>
              <Dropdown.Toggle
                as="div"
                role="button"
                disabled={loading}
                id={`status-dropdown-${index}`}
                className="status-dropdown-toggle"
                style={{
                  backgroundColor: currentStatus?.color || '#6c757d',
                  color: '#fff',
                  width: '160px'
                }}>
                {currentStatus?.name || 'Select Status'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '150px' }}>
                {statuses?.map((status) => (
                  <Dropdown.Item
                    key={status.id}
                    onClick={() => onDropdownChange(status.id)}
                    className={`d-flex align-items-center custom-dropdown-item ${
                      status.id === currentStatus?.id ? 'selected' : ''
                    }`}>
                    <Circle
                      sx={{ color: status.color, fontSize: '1rem', mr: 1 }}
                    />
                    {status.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <div
              className="status-label"
              style={{
                backgroundColor: script.status_details?.color || 'transparent',
              }}>
              {script.status_details?.name || 'No Status'}
            </div>
          )}
        </td>
        <td>
          <CustomIconButton
            size="sm"
            title={attachments?.length > 0 ? 'View Attachments' : 'No Attachments'}
            sx={{
              color: attachments?.length > 0 ? '#1b3f87' : 'grey',
            }}
            disabled={attachments?.length === 0}
            onClick={() => setOpenAttachments(true)}>
            <Attachment />
          </CustomIconButton>
        </td>

      <style>{`
        .wrap-cell {
          word-break: break-word;
          white-space: pre-wrap;
          vertical-align: top;
          padding: 8px;
        }

        .actual-result-readonly {
          white-space: pre-wrap;
          word-break: break-word;
          padding: 4px;
        }

        .actual-result-readonly.clickable:hover {
          border: 1px solid #ced4da;
          background: #f8f9fa;
          border-radius: 0.25rem;
          cursor: pointer;
          min-height: 32px;
        }

        .status-label {
          color: #fff;
          padding: 6px 12px;
          border-radius: 4px;
          text-align: center;
          width: 100%;
        }

        .status-dropdown-toggle {
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
        }

        .custom-dropdown-item.selected {
          font-weight: bold;
        }

        /* New style for clickable links in table cells */
        .table-link {
          color: #007bff; /* Standard link blue */
          text-decoration: underline;
          cursor: pointer;
        }
        .table-link:hover {
          color: #0056b3; /* Darker blue on hover */
        }
      `}</style>
    </tr>
      <Modal
        open={openAttachments}
        handleClose={() => setOpenAttachments(false)}
        width="lg"
        modalTitle="Test Step Attachments">
        <Container className="px-0">
          <Typography variant="subtitle1" color="textSecondary">
            Step {script?.teststeps?.s_no}
          </Typography>
          <AttachmentTable
            data={
              (attachments &&
                attachments.map((att) => ({
                  ...att,
                  owner: getDisplayUserName(att?.user_detail),
                  file_name: att.file_name.split('.')[0],
                }))) ||
              []
            }
            headerTabs={attachmentTableHeaders}
            isLoading={loading}
            defaultSort="created_at"
            order={order}
            setOrder={setOrder}
            editableFields={['file_name']}
            isEditable={false}
          />
        </Container>
      </Modal>
    </>
  );
};

export default TestStepItem;